import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { captureException, captureMessage } from '@sentry/react';
import { startsWith } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import AbsenceIcon from '@/assets/icons/absence.svg?react';
import AnnouncementIcon from '@/assets/icons/announcement.svg?react';
import MenuIcon from '@/assets/icons/menu.svg?react';
import NumberBadge from '@/components/ui/badge/NumberBadge';
import Dropdown from '@/components/ui/dropdown/Dropdown';
import DropdownItem from '@/components/ui/dropdown/DropdownItem';
import DropdownMenu from '@/components/ui/dropdown/DropdownMenu';
import DropdownToggle from '@/components/ui/dropdown/DropdownToggle';
import Nav from '@/components/ui/mainHeader/Nav';
import NavItem from '@/components/ui/mainHeader/NavItem';
import { pageTitles } from '@/constants/pageTitles';
import useAuthentication from '@/hooks/common/useAuthentication';
import useFetch from '@/hooks/common/useFetch';
import useReactQuery from '@/hooks/common/useReactQuery';
import { useDarklaunchV2VariationByOrganizationIds } from '@/hooks/useDarklaunchV2';
import { AppContext } from '@/providers/ContextProvider';

import CSSModule from './MainHeaderNav.module.scss';
import ShinroNavItem from './ShinroNavItem';

interface Props {
  isHidden?: boolean;
  showNumberBadge?: boolean;
}

export interface UnreadNoticesCount {
  unread_count: number;
}

interface UnreadAnnouncementsCount {
  unread_count: number;
}

const MainHeaderNav: React.FC<Props> = ({
  isHidden = false,
  showNumberBadge = true,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { clearUserData, isLoggedIn } = useAuthentication();
  const { useQuery } = useReactQuery();

  const { fetch, isFetching, status } = useFetch(
    'DELETE',
    '/api/school/auth/token',
  );

  const deleteAccessToken = useCallback(() => {
    void fetch();
  }, [fetch]);

  const { data, error, errorUpdatedAt } = useQuery<UnreadNoticesCount>({
    queryKey: ['unread_count'],
    url: '/api/school/notice_from_managements/unread_count',
    enabled: isLoggedIn,
    staleTime: 0,
  });

  useEffect(() => {
    if (error && errorUpdatedAt) {
      captureMessage(`Failed to fetch unreadNoticesCount: ${error.error_code}`);
      captureException(error || 'unreadNoticesCount: something wrong');
    }
  }, [error, errorUpdatedAt]);

  const unreadCount = data?.unread_count;

  const {
    data: announceUnreadData,
    error: announceUnreadError,
    errorUpdatedAt: annouceUnreadErrorAt,
  } = useQuery<UnreadAnnouncementsCount>({
    queryKey: ['announcements_unread_count'],
    url: '/api/school_communication/announcements/unread_count',
    enabled: isLoggedIn,
    staleTime: 0,
  });

  useEffect(() => {
    if (announceUnreadError && annouceUnreadErrorAt) {
      captureMessage(
        `Failed to fetch announcementsUnreadCount: ${announceUnreadError.error_code}`,
      );
      captureException(
        announceUnreadError || 'announcementsUnreadCount: something wrong',
      );
    }
  }, [announceUnreadError, annouceUnreadErrorAt]);

  const isAnnounceUnread = !!announceUnreadData?.unread_count;

  const { currentUser } = useContext(AppContext);
  const isAllowedAbsence = useMemo(
    () =>
      currentUser?.organization_memberships.some((om) =>
        Boolean(om.organization_management?.allowed_absence),
      ),
    [currentUser],
  );

  const { variation } = useDarklaunchV2VariationByOrganizationIds(
    'enable-school-comm-fp-shingaku-preview',
    currentUser?.organization_memberships.map((om) => om.organization_id) ?? [],
  );

  const isAllowedShinroInformation = useMemo(
    () =>
      currentUser?.organization_memberships.some((om) =>
        Boolean(variation[om.organization_id]),
      ) ?? false,
    [currentUser, variation],
  );

  useEffect(() => {
    if (status === 204) {
      clearUserData();
      navigate('/login');
    }
    // navigateを入れると2回発火するため
  }, [clearUserData, status]);
  if (isHidden || !isLoggedIn) return null;

  return (
    <Nav>
      <NavItem
        icon={<AnnouncementIcon />}
        label={intl.formatMessage({ id: 'header.nav.announcements' })}
        url="/announcements"
        active={startsWith(location.pathname, '/announcements')}
        badgeShowEmpty={isAnnounceUnread}
      />
      {isAllowedAbsence && (
        <NavItem
          icon={<AbsenceIcon />}
          label={intl.formatMessage({ id: 'header.nav.absences' })}
          url="/absences"
          active={startsWith(location.pathname, '/absences')}
        />
      )}
      {isAllowedShinroInformation && <ShinroNavItem />}
      <Dropdown>
        <DropdownToggle>
          <NavItem
            icon={<MenuIcon />}
            label={intl.formatMessage({ id: 'header.nav.menu' })}
            active={startsWith(location.pathname, '/mypage/profile')}
          />
        </DropdownToggle>
        {!!unreadCount && showNumberBadge && (
          <div
            className={CSSModule.NavItem__NumberBadge}
            data-testid="navitem_number_badge"
          >
            <NumberBadge showEmpty />
          </div>
        )}
        <DropdownMenu className={CSSModule.MainHeaderNav__DropDownMenu}>
          <DropdownItem url="/notices">
            <div className={CSSModule.DropDownItem__Notices}>
              <div>運営からのお知らせ</div>
              {!!unreadCount && showNumberBadge && (
                <div data-testid="dropdown_notices_number_badge">
                  <NumberBadge showEmpty />
                </div>
              )}
            </div>
          </DropdownItem>
          <DropdownItem url="/mypage/profile">アカウント設定</DropdownItem>
          <DropdownItem url="/contact">{pageTitles.contact}</DropdownItem>
          <DropdownItem onClick={deleteAccessToken} disabled={isFetching}>
            ログアウト
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </Nav>
  );
};

export default MainHeaderNav;
